import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PinDropIcon from '@mui/icons-material/PinDrop';
import SearchIcon from '@mui/icons-material/Search';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  CircularProgress,
  InputBase,
  Paper,
  Tooltip as MuiTooltip,
  Typography,
  type TooltipProps,
} from '@mui/material';
import { useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  useGrowthDecline,
  type GrowthDeclineAccount,
  type GrowthDeclineResponse,
} from '../api/depletions';
import { useClickOutside } from '../hooks/useClickOutside';
import { fmt6PkNumber, toSixPacks } from '../lib/volume';
import { colors } from '../theme';
import {
  accountColumns,
  excelSheet,
  num as toNum,
  premisesLabel,
  type ExcelColumn,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';
import { PremisesBadge } from './PremisesBadge';
import { useSectionExport } from '../hooks/useSectionExport';
import { SectionExplainer } from './SectionExplainer';

const num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

// ----------------------------------------------------------------
// Premium light tooltip
// ----------------------------------------------------------------

interface RichTooltipProps {
  title: TooltipProps['title'];
  children: ReactElement;
  placement?: TooltipProps['placement'];
  maxWidth?: number;
}

function Tooltip({ title, children, placement = 'top', maxWidth = 280 }: RichTooltipProps) {
  return (
    <MuiTooltip
      title={title}
      placement={placement}
      arrow
      enterDelay={120}
      enterNextDelay={50}
      leaveDelay={40}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.05)',
            fontSize: 11.5,
            lineHeight: 1.45,
            maxWidth,
            padding: '8px 10px',
            fontFeatureSettings: '"tnum","lnum"',
          },
        },
        arrow: {
          sx: {
            color: '#fff',
            '&::before': {
              border: `1px solid ${colors.border}`,
              backgroundColor: '#fff',
            },
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
}

// ----------------------------------------------------------------
// Structured tooltip block — gives portfolio tiles a docs-style
// layout (title + label/description rows + italic footer) instead
// of free-form prose.
// ----------------------------------------------------------------

interface TooltipBlockProps {
  title?: string;
  items?: Array<{ label: string; description: string }>;
  bullets?: string[];
  tip?: string;
  maxWidth?: number;
}

function TooltipBlock({ title, items, bullets, tip, maxWidth = 260 }: TooltipBlockProps) {
  return (
    <Box sx={{ p: 0.25, maxWidth }}>
      {title && (
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.textPrimary, mb: 0.75 }}>
          {title}
        </Typography>
      )}
      {items && items.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.6,
            mb: bullets || tip ? 0.75 : 0,
          }}
        >
          {items.map((it) => (
            <Box key={it.label}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1.3,
                }}
              >
                {it.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}>
                {it.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {bullets && bullets.length > 0 && (
        <Box
          component="ul"
          sx={{
            pl: 0,
            m: 0,
            mb: tip ? 0.75 : 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.3,
          }}
        >
          {bullets.map((b, i) => (
            <Box
              component="li"
              key={i}
              sx={{
                fontSize: 11,
                color: colors.textSecondary,
                lineHeight: 1.4,
                position: 'relative',
                pl: 1.25,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 2,
                  top: 7,
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: colors.textMuted,
                },
              }}
            >
              {b}
            </Box>
          ))}
        </Box>
      )}
      {tip && (
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textMuted,
            fontStyle: 'italic',
            lineHeight: 1.4,
            borderTop: `1px solid ${colors.borderLight}`,
            pt: 0.6,
            mt: items || bullets ? 0.5 : 0,
          }}
        >
          {tip}
        </Typography>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Portfolio tile
// ----------------------------------------------------------------

interface PortfolioTileProps {
  eyebrow: string;
  primary: ReactNode;
  primaryColor?: string;
  secondary?: ReactNode;
  tooltip?: ReactNode;
}

function PortfolioTile({ eyebrow, primary, primaryColor, secondary, tooltip }: PortfolioTileProps) {
  const body = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 1.5,
        borderRight: `1px solid ${colors.borderLight}`,
        '&:last-of-type': { borderRight: 'none' },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        cursor: tooltip ? 'help' : 'default',
        transition: 'background-color 0.12s',
        '&:hover': tooltip ? { bgcolor: '#fff' } : undefined,
      }}
    >
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: colors.textMuted,
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </Typography>
      <Box
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: primaryColor ?? colors.chartPrimary,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFeatureSettings: '"tnum","lnum"',
        }}
      >
        {primary}
      </Box>
      {secondary !== undefined && (
        <Box sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.3 }}>{secondary}</Box>
      )}
    </Box>
  );
  if (!tooltip) return body;
  return <Tooltip title={tooltip}>{body}</Tooltip>;
}

// ----------------------------------------------------------------
// Toolbar primitives
// ----------------------------------------------------------------

interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

function SearchInput({ value, onChange, placeholder }: SearchInputProps) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.4,
        borderRadius: 1,
        border: `1px solid ${value ? colors.chartPrimary : colors.border}`,
        bgcolor: '#fff',
        minWidth: 240,
        '&:hover': { borderColor: colors.chartPrimary },
        '&:focus-within': {
          borderColor: colors.chartPrimary,
          boxShadow: `0 0 0 3px ${colors.chartPrimary}1a`,
        },
      }}
    >
      <SearchIcon sx={{ fontSize: 16, color: colors.textMuted }} />
      <InputBase
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          flex: 1,
          fontSize: 12,
          color: colors.textPrimary,
          '& input::placeholder': { color: colors.textMuted, opacity: 1, fontSize: 11.5 },
        }}
      />
      {value && (
        <Box
          component="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            color: colors.textMuted,
            '&:hover': { bgcolor: colors.surfaceHover, color: colors.textPrimary },
          }}
        >
          <ClearIcon sx={{ fontSize: 13 }} />
        </Box>
      )}
    </Box>
  );
}

interface StateMultiSelectProps {
  available: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  open: boolean;
  onToggle: () => void;
}

function StateMultiSelect({
  available,
  selected,
  onChange,
  open,
  onToggle,
}: StateMultiSelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, onToggle, open);
  const valueLabel =
    selected.size === 0
      ? 'All states'
      : selected.size === 1
        ? [...selected][0]
        : `${selected.size} states`;
  const toggle = (code: string) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(next);
  };
  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${open || selected.size > 0 ? colors.chartPrimary : colors.border}`,
          bgcolor: open || selected.size > 0 ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 12,
          fontWeight: 500,
          color: colors.textPrimary,
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 11 }}>
          State:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {valueLabel}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 10,
            minWidth: 280,
            maxWidth: 360,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
            p: 1.25,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 0.75,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.textMuted }}>
              {available.length} states
            </Typography>
            {selected.size > 0 && (
              <Box
                component="button"
                onClick={() => onChange(new Set())}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.chartPrimary,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear all
              </Box>
            )}
          </Box>
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 240, overflow: 'auto' }}
          >
            {available.map((code) => {
              const isSel = selected.has(code);
              return (
                <Box
                  key={code}
                  component="button"
                  onClick={() => toggle(code)}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    px: 1,
                    py: 0.4,
                    borderRadius: 0.75,
                    border: `1px solid ${isSel ? colors.chartPrimary : colors.border}`,
                    bgcolor: isSel ? colors.chartPrimary : '#fff',
                    color: isSel ? '#fff' : colors.textPrimary,
                    fontSize: 11.5,
                    fontWeight: isSel ? 700 : 500,
                    fontFeatureSettings: '"tnum"',
                    '&:hover': {
                      borderColor: colors.chartPrimary,
                      bgcolor: isSel ? colors.chartPrimary : `${colors.chartPrimary}10`,
                    },
                  }}
                >
                  {code}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Inline insight row — compact geographic callouts. Per-account
// call lists live in the Follow-Up Tracker so the rep has one hub.
// ----------------------------------------------------------------

interface InsightChip {
  key: string;
  Icon: typeof TrendingUpIcon;
  color: string;
  label: string;
  primary: string;
  detail: string;
}

interface InsightRowProps {
  eyebrow: string;
  chips: InsightChip[];
  followUpHint?: string;
}

function InsightRow({ eyebrow, chips, followUpHint }: InsightRowProps) {
  if (chips.length === 0 && !followUpHint) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        px: 1.5,
        py: 1,
        mb: 2.5,
        borderRadius: 1.25,
        bgcolor: colors.surfaceSubtle,
        border: `1px solid ${colors.borderLight}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          mr: 0.5,
        }}
      >
        {eyebrow}
      </Typography>
      {chips.map((c, i) => (
        <Box
          key={c.key}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            pr: 1,
            borderRight: i === chips.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: 0.75,
              bgcolor: `${c.color}14`,
              color: c.color,
              flexShrink: 0,
            }}
          >
            <c.Icon sx={{ fontSize: 13 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                {c.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: c.color,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {c.primary}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.textSecondary,
                lineHeight: 1.3,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {c.detail}
            </Typography>
          </Box>
        </Box>
      ))}
      <Box sx={{ flex: 1 }} />
      {followUpHint && (
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textMuted,
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
          }}
        >
          {followUpHint}
        </Typography>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Account row + panel
// ----------------------------------------------------------------

interface RowProps {
  acc: GrowthDeclineAccount;
  rank: number;
  color: string;
  scaleMax: number;
  declining: boolean;
}

function Row({ acc, rank, color, scaleMax, declining }: RowProps) {
  const diff = Number(acc.diff_9l);
  const recent = Number(acc.recent_9l);
  const prior = Number(acc.prior_9l);
  const barW = scaleMax > 0 ? (Math.abs(diff) / scaleMax) * 100 : 0;
  const pct = prior > 0 ? (diff / prior) * 100 : null;
  const cityState = [acc.state_code, acc.city].filter(Boolean).join(' · ') || '—';

  return (
    <Tooltip
      placement="left"
      title={
        <Box sx={{ p: 0.25, maxWidth: 260 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.4 }}>{acc.name}</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto',
              gap: '4px 12px',
              fontSize: 11,
              fontFeatureSettings: '"tnum"',
            }}
          >
            <span style={{ opacity: 0.7 }}>Location</span>
            <span style={{ fontWeight: 600 }}>{cityState}</span>
            <span style={{ opacity: 0.7 }}>Distributor</span>
            <span style={{ fontWeight: 600 }}>{acc.distributor_name ?? '—'}</span>
            <span style={{ opacity: 0.7 }}>Prior window</span>
            <span style={{ fontWeight: 600 }}>
              {fmt6PkNumber(prior)} 6-pk · {num.format(prior)} 9L
            </span>
            <span style={{ opacity: 0.7 }}>Recent window</span>
            <span style={{ fontWeight: 600 }}>
              {fmt6PkNumber(recent)} 6-pk · {num.format(recent)} 9L
            </span>
            <span style={{ opacity: 0.7 }}>Change</span>
            <span style={{ fontWeight: 700, color }}>
              {diff >= 0 ? '+' : ''}
              {fmt6PkNumber(diff)} 6-pk · {diff >= 0 ? '+' : ''}
              {num.format(diff)} 9L
              {pct !== null && (
                <Box component="span" sx={{ ml: 0.5, color: colors.textMuted, fontWeight: 500 }}>
                  ({pct >= 0 ? '+' : ''}
                  {pct.toFixed(0)}%)
                </Box>
              )}
            </span>
          </Box>
          <Typography
            sx={{ fontSize: 10.5, color: colors.textMuted, mt: 0.5, fontStyle: 'italic' }}
          >
            {declining
              ? 'Already a customer; orders dropped vs the prior window.'
              : 'Already a customer; orders grew vs the prior window.'}
          </Typography>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.85,
          borderBottom: `1px solid ${colors.borderLight}`,
          '&:last-of-type': { borderBottom: 'none' },
          cursor: 'help',
          transition: 'background-color 0.12s',
          '&:hover': { bgcolor: `${color}0a` },
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: colors.textMuted,
            width: 22,
            textAlign: 'right',
            flexShrink: 0,
            fontFeatureSettings: '"tnum"',
            opacity: 0.7,
          }}
        >
          {rank}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {acc.name}
            </Typography>
            <PremisesBadge premises={acc.premises_type} online={acc.is_online_retailer} />
          </Box>
          <Typography
            sx={{ fontSize: 10.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}
          >
            {cityState} · {fmt6PkNumber(prior)} → {fmt6PkNumber(recent)} 6-pk ({num.format(prior)} →{' '}
            {num.format(recent)} 9L)
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
            width: 140,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: 5,
              bgcolor: `${color}1a`,
              borderRadius: 3,
              overflow: 'hidden',
              direction: declining ? 'rtl' : 'ltr',
            }}
          >
            <Box
              sx={{
                width: `${barW}%`,
                height: '100%',
                borderRadius: 3,
                bgcolor: color,
                opacity: 0.65,
              }}
            />
          </Box>
          <Box sx={{ minWidth: 64, textAlign: 'right' }}>
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                color,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {diff > 0 ? '+' : ''}
              {num.format(diff)}
            </Typography>
            {pct !== null && (
              <Typography
                sx={{
                  fontSize: 9.5,
                  color: colors.textMuted,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {pct >= 0 ? '+' : ''}
                {pct.toFixed(0)}%
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}

interface PanelProps {
  title: string;
  caption: string;
  icon: typeof TrendingUpIcon;
  color: string;
  accounts: GrowthDeclineAccount[];
  totalCount: number;
  totalVolume: number;
  declining: boolean;
}

function Panel({
  title,
  caption,
  icon: Icon,
  color,
  accounts,
  totalCount,
  totalVolume,
  declining,
}: PanelProps) {
  const scaleMax = accounts.reduce((acc, a) => Math.max(acc, Math.abs(Number(a.diff_9l))), 0) || 1;
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: `1px solid ${color}33`,
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          bgcolor: `${color}08`,
          borderBottom: `1px solid ${color}26`,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            bgcolor: `${color}1a`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: colors.textPrimary }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>{caption}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color,
              lineHeight: 1,
              fontFeatureSettings: '"tnum"',
              letterSpacing: '-0.02em',
            }}
          >
            {totalCount}
          </Typography>
          <Typography sx={{ fontSize: 10, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
            {declining ? '−' : '+'}
            {fmt6PkNumber(totalVolume)} 6-pk · {declining ? '−' : '+'}
            {num.format(totalVolume)} 9L
          </Typography>
        </Box>
      </Box>
      {accounts.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>
            No accounts match the current filters.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
          {accounts.map((a, i) => (
            <Row
              key={a.account_id}
              acc={a}
              rank={i + 1}
              color={color}
              scaleMax={scaleMax}
              declining={declining}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Main component
// ----------------------------------------------------------------

function growthDeclineColumns(windowMonths: number): ExcelColumn<GrowthDeclineAccount>[] {
  return [
    ...accountColumns<GrowthDeclineAccount>(),
    { header: 'Distributor', value: (r) => r.distributor_name, width: 24 },
    { header: 'Premises', value: (r) => premisesLabel(r.premises_type, r.is_online_retailer) },
    {
      header: `Prior 9L cases (${windowMonths} mo)`,
      value: (r) => toNum(r.prior_9l),
      format: 'decimal',
    },
    {
      header: `Recent 9L cases (${windowMonths} mo)`,
      value: (r) => toNum(r.recent_9l),
      format: 'decimal',
    },
    { header: 'Change (9L cases)', value: (r) => toNum(r.diff_9l), format: 'decimal' },
    {
      header: 'Change (6-packs)',
      value: (r) => Math.round(toSixPacks(r.diff_9l)),
      format: 'int',
    },
    {
      header: '% change',
      value: (r) => {
        const prior = toNum(r.prior_9l);
        const diff = toNum(r.diff_9l);
        return prior && diff !== null ? diff / prior : null;
      },
      format: 'percent',
    },
  ];
}

function buildGrowthDeclineExport(
  data: GrowthDeclineResponse,
  visibleGrowing: GrowthDeclineAccount[],
  visibleDeclining: GrowthDeclineAccount[],
  filters: ExportFilter[],
): ExcelWorkbookSpec {
  const columns = growthDeclineColumns(data.window_months);
  return {
    title: 'Growth & Decline',
    filters,
    sheets: [
      excelSheet({ name: 'Growing', columns, rows: visibleGrowing }),
      excelSheet({ name: 'Declining', columns, rows: visibleDeclining }),
    ],
  };
}

export function GrowthDeclineSection() {
  const { data, isLoading, error } = useGrowthDecline(12);
  const [search, setSearch] = useState('');
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set());
  const [stateMenuOpen, setStateMenuOpen] = useState(false);

  const growing = useMemo(() => data?.growing_accounts ?? [], [data?.growing_accounts]);
  const declining = useMemo(() => data?.declining_accounts ?? [], [data?.declining_accounts]);
  const growingVol = Number(data?.growing_volume ?? 0);
  const decliningVol = Number(data?.declining_volume ?? 0);
  const netVol = growingVol - decliningVol;

  // Concentration metrics — share of growth/decline coming from the
  // top-3 accounts in each list. > 50% flags as fragile.
  const topGrowingShare = useMemo(() => {
    if (growingVol <= 0) return 0;
    const top = growing
      .slice()
      .sort((a, b) => Number(b.diff_9l) - Number(a.diff_9l))
      .slice(0, 3)
      .reduce((s, a) => s + Number(a.diff_9l), 0);
    return top / growingVol;
  }, [growing, growingVol]);
  const topDecliningShare = useMemo(() => {
    if (decliningVol <= 0) return 0;
    const top = declining
      .slice()
      .sort((a, b) => Math.abs(Number(b.diff_9l)) - Math.abs(Number(a.diff_9l)))
      .slice(0, 3)
      .reduce((s, a) => s + Math.abs(Number(a.diff_9l)), 0);
    return top / decliningVol;
  }, [declining, decliningVol]);

  // State-level aggregation across both directions so we can identify
  // states where the net is positive vs negative.
  const stateNetMap = useMemo(() => {
    const map = new Map<string, { growVol: number; declineVol: number }>();
    for (const a of growing) {
      if (!a.state_code) continue;
      const cur = map.get(a.state_code) ?? { growVol: 0, declineVol: 0 };
      cur.growVol += Number(a.diff_9l);
      map.set(a.state_code, cur);
    }
    for (const a of declining) {
      if (!a.state_code) continue;
      const cur = map.get(a.state_code) ?? { growVol: 0, declineVol: 0 };
      cur.declineVol += Math.abs(Number(a.diff_9l));
      map.set(a.state_code, cur);
    }
    return map;
  }, [growing, declining]);

  const stateGainers = useMemo(
    () =>
      [...stateNetMap.entries()]
        .map(([code, v]) => ({ code, net: v.growVol - v.declineVol }))
        .filter((s) => s.net > 0)
        .sort((a, b) => b.net - a.net),
    [stateNetMap],
  );
  const stateLosers = useMemo(
    () =>
      [...stateNetMap.entries()]
        .map(([code, v]) => ({ code, net: v.growVol - v.declineVol }))
        .filter((s) => s.net < 0)
        .sort((a, b) => a.net - b.net),
    [stateNetMap],
  );

  const availableStates = useMemo(() => {
    const set = new Set<string>();
    for (const a of growing) if (a.state_code) set.add(a.state_code);
    for (const a of declining) if (a.state_code) set.add(a.state_code);
    return [...set].sort();
  }, [growing, declining]);

  const filterList = (rows: GrowthDeclineAccount[]) => {
    const q = search.trim().toLowerCase();
    return rows.filter((a) => {
      if (q) {
        const hit =
          a.name.toLowerCase().includes(q) ||
          (a.city ?? '').toLowerCase().includes(q) ||
          (a.state_code ?? '').toLowerCase().includes(q) ||
          (a.distributor_name ?? '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (selectedStates.size > 0) {
        if (!a.state_code || !selectedStates.has(a.state_code)) return false;
      }
      return true;
    });
  };
  const visibleGrowing = useMemo(
    () => filterList(growing).sort((a, b) => Number(b.diff_9l) - Number(a.diff_9l)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [growing, search, selectedStates],
  );
  const visibleDeclining = useMemo(
    () =>
      filterList(declining).sort(
        (a, b) => Math.abs(Number(b.diff_9l)) - Math.abs(Number(a.diff_9l)),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [declining, search, selectedStates],
  );

  useSectionExport(
    () =>
      data
        ? buildGrowthDeclineExport(data, visibleGrowing, visibleDeclining, [
            {
              label: 'Search (name, city, state or distributor)',
              value: search.trim() ? `"${search.trim()}"` : '',
            },
            {
              label: 'State',
              value: selectedStates.size > 0 ? [...selectedStates].sort().join(', ') : 'All states',
            },
            {
              kind: 'sort',
              label: 'Sorted by',
              value: 'Size of change in 9L cases (largest → smallest)',
            },
            {
              kind: 'info',
              label: 'Comparison window',
              value: `Last ${data.window_months} months vs prior ${data.window_months} months`,
            },
            {
              kind: 'info',
              label: 'Recent window',
              value: `${data.recent_window_start} to ${data.reference_date}`,
            },
            {
              kind: 'info',
              label: 'Prior window',
              value: `${data.prior_window_start} to ${data.prior_window_end}`,
            },
            { kind: 'info', label: 'Data through', value: data.reference_date },
          ])
        : null,
    !!data,
  );

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, border: `1px solid ${colors.error}40`, bgcolor: `${colors.error}05` }}
      >
        <Typography color="error">Failed to load growth &amp; decline: {error.message}</Typography>
      </Paper>
    );
  }
  if (isLoading || !data) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Account Growth &amp; Decline
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${colors.chartPrimary}14`,
          }}
        >
          <TrendingUpIcon sx={{ color: colors.chartPrimary, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Account Growth &amp; Decline
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Returning accounts compared across a {data.window_months}-month window vs the equivalent
            prior window. Net change tells you whether the existing base is expanding or shrinking.
          </Typography>
        </Box>
      </Box>

      {/* Portfolio strip */}
      <Box
        sx={{
          display: 'flex',
          border: `1px solid ${colors.borderLight}`,
          borderRadius: 1.5,
          mb: 2.5,
          overflow: 'hidden',
          bgcolor: colors.surfaceSubtle,
        }}
      >
        <PortfolioTile
          eyebrow="Net momentum"
          primary={`${netVol >= 0 ? '+' : ''}${fmt6PkNumber(netVol)} 6-pk`}
          primaryColor={netVol >= 0 ? colors.success : colors.error}
          secondary={
            <>
              {netVol >= 0 ? '+' : ''}
              {fmt6PkNumber(netVol)} 6-pk · {data.growing_count} growing · {data.declining_count}{' '}
              declining
            </>
          }
          tooltip={
            <TooltipBlock
              title="Net momentum"
              items={[
                {
                  label: 'Definition',
                  description:
                    '6-pk gained by growing returning accounts minus 6-pk lost by declining returning accounts.',
                },
                {
                  label: 'This window',
                  description: `+${fmt6PkNumber(growingVol)} 6-pk from ${data.growing_count} growing · −${fmt6PkNumber(decliningVol)} 6-pk from ${data.declining_count} declining`,
                },
              ]}
              tip="Positive means the existing customer base is expanding."
            />
          }
        />
        <PortfolioTile
          eyebrow="Growth concentration"
          primary={`${Math.round(topGrowingShare * 100)}%`}
          primaryColor={topGrowingShare > 0.5 ? colors.warning : colors.success}
          secondary={
            <>
              Top-3 share of growth ·{' '}
              <strong
                style={{ color: topGrowingShare > 0.5 ? colors.warning : colors.textSecondary }}
              >
                {topGrowingShare > 0.5 ? 'Concentrated' : 'Diversified'}
              </strong>
            </>
          }
          tooltip={
            <TooltipBlock
              title="Growth concentration"
              items={[
                {
                  label: 'Definition',
                  description: 'Share of total growth coming from the top-3 growing accounts.',
                },
              ]}
              bullets={[
                '≤ 50% — diversified growth, multiple accounts pulling',
                '> 50% — fragile, dependent on a small group',
              ]}
              tip="When concentrated, losing one big grower can undo the whole trend."
            />
          }
        />
        <PortfolioTile
          eyebrow="Loss concentration"
          primary={`${Math.round(topDecliningShare * 100)}%`}
          primaryColor={topDecliningShare > 0.5 ? colors.error : colors.warning}
          secondary={
            <>
              Top-3 share of decline ·{' '}
              <strong
                style={{ color: topDecliningShare > 0.5 ? colors.error : colors.textSecondary }}
              >
                {topDecliningShare > 0.5 ? 'Concentrated' : 'Diversified'}
              </strong>
            </>
          }
          tooltip={
            <TooltipBlock
              title="Loss concentration"
              items={[
                {
                  label: 'Definition',
                  description: 'Share of total decline coming from the top-3 declining accounts.',
                },
              ]}
              bullets={[
                '≤ 50% — broad-based slowdown',
                '> 50% — recovery effort can focus on a small list',
              ]}
              tip="When concentrated, three well-targeted calls can flip the trajectory."
            />
          }
        />
        <PortfolioTile
          eyebrow="State shift"
          primary={`${stateGainers.length}↑ · ${stateLosers.length}↓`}
          primaryColor={
            stateGainers.length > stateLosers.length
              ? colors.success
              : stateGainers.length < stateLosers.length
                ? colors.error
                : colors.textPrimary
          }
          secondary={<>States with net gain vs net loss</>}
          tooltip={
            <TooltipBlock
              title="State shift"
              items={[
                {
                  label: 'Definition',
                  description:
                    'Number of states with positive vs negative net 6-pk change in this window.',
                },
                {
                  label: 'This window',
                  description: `${stateGainers.length} state${stateGainers.length === 1 ? '' : 's'} with net gain · ${stateLosers.length} with net loss`,
                },
              ]}
              tip="Large asymmetry signals a geographic shift in the portfolio."
            />
          }
        />
      </Box>

      {/* Compact insight row — geographic patterns. Per-account call
          lists ("defend the growers", "recover decliners") live in
          the Follow-Up Tracker via the Defend / At-risk chips. */}
      <InsightRow
        eyebrow="Patterns"
        chips={[
          ...(stateGainers[0]
            ? [
                {
                  key: 'hot-1',
                  Icon: LocalFireDepartmentIcon,
                  color: colors.success,
                  label: 'Hot market',
                  primary: stateGainers[0].code,
                  detail: `+${fmt6PkNumber(stateGainers[0].net)} 6-pk net`,
                },
              ]
            : []),
          ...(stateGainers[1]
            ? [
                {
                  key: 'hot-2',
                  Icon: LocalFireDepartmentIcon,
                  color: colors.success,
                  label: 'Also hot',
                  primary: stateGainers[1].code,
                  detail: `+${fmt6PkNumber(stateGainers[1].net)} 6-pk net`,
                },
              ]
            : []),
          ...(stateLosers[0]
            ? [
                {
                  key: 'cool-1',
                  Icon: PinDropIcon,
                  color: colors.error,
                  label: 'Cool market',
                  primary: stateLosers[0].code,
                  detail: `${fmt6PkNumber(stateLosers[0].net)} 6-pk net`,
                },
              ]
            : []),
          ...(stateLosers[1]
            ? [
                {
                  key: 'cool-2',
                  Icon: PinDropIcon,
                  color: colors.error,
                  label: 'Also cool',
                  primary: stateLosers[1].code,
                  detail: `${fmt6PkNumber(stateLosers[1].net)} 6-pk net`,
                },
              ]
            : []),
        ]}
        followUpHint="Per-account growers/decliners are tagged in the Follow-Up Tracker."
      />

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, city, state, or distributor…"
        />
        <StateMultiSelect
          available={availableStates}
          selected={selectedStates}
          onChange={setSelectedStates}
          open={stateMenuOpen}
          onToggle={() => setStateMenuOpen((o) => !o)}
        />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
          {visibleGrowing.length} growing · {visibleDeclining.length} declining in view
        </Typography>
      </Box>

      {/* Two-column list */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Panel
          title="Growing"
          caption="Returning accounts where recent-window 6-pk > prior-window 6-pk"
          icon={TrendingUpIcon}
          color={colors.success}
          accounts={visibleGrowing}
          totalCount={data.growing_count}
          totalVolume={growingVol}
          declining={false}
        />
        <Panel
          title="Declining"
          caption="Returning accounts where recent-window 6-pk < prior-window 6-pk"
          icon={TrendingDownIcon}
          color={colors.error}
          accounts={visibleDeclining}
          totalCount={data.declining_count}
          totalVolume={decliningVol}
          declining={true}
        />
      </Box>

      <SectionExplainer
        rows={[
          {
            sample: <GDSampleGrowingBadge />,
            term: '"Growing" accounts',
            desc: (
              <>
                Active in BOTH windows AND recent-window 6-pk &gt; prior-window 6-pk.{' '}
                <em>Existing customers</em> who ordered more lately — pair with Top Accounts to find
                biggest movers.
              </>
            ),
          },
          {
            sample: <GDSampleDecliningBadge />,
            term: '"Declining" accounts',
            desc: (
              <>
                Active in BOTH windows AND recent-window 6-pk &lt; prior-window 6-pk.{' '}
                <em>Existing customers</em> who slowed down — the front-line at-risk pool, even if
                not yet silent.
              </>
            ),
          },
          {
            sample: <GDSampleDiffBar />,
            term: 'Diff bar per row',
            desc: (
              <>
                Compact horizontal bar showing the absolute 6-pk change vs the most-changed account
                in the panel. Bigger bar = bigger shift. Sorted by magnitude so the largest movers
                sit on top.
              </>
            ),
          },
          {
            sample: <GDSamplePortfolioTile />,
            term: 'Portfolio strip',
            desc: (
              <>
                Top tiles: <strong>Net momentum</strong> (gained − lost 6-pk),{' '}
                <strong>Growth concentration</strong> (top-3 growers share),{' '}
                <strong>Loss concentration</strong>, <strong>State shift</strong>. Reads as the
                section's one-line summary.
              </>
            ),
          },
          {
            sample: (
              <Box
                component="span"
                sx={{
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  px: 0.6,
                  py: 0.15,
                  borderRadius: 0.75,
                  color: colors.success,
                  bgcolor: `${colors.success}14`,
                  border: `1px solid ${colors.success}40`,
                  lineHeight: 1.4,
                }}
              >
                ON
              </Box>
            ),
            term: 'Premises badge per account',
            desc: (
              <>
                The channel classification beside the name. <strong>ON</strong> bars/restaurants,{' '}
                <strong>OFF</strong> liquor stores/retail, <strong>Online</strong> e-commerce
                retailer (violet), <strong>NA</strong> neither.
              </>
            ),
          },
        ]}
      />
    </Paper>
  );
}

// Growth/Decline sample swatches
function GDSampleGrowingBadge() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: colors.success,
        bgcolor: `${colors.success}14`,
        border: `1px solid ${colors.success}40`,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      ↑ Up
    </Box>
  );
}
function GDSampleDecliningBadge() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: colors.error,
        bgcolor: `${colors.error}14`,
        border: `1px solid ${colors.error}40`,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      ↓ Down
    </Box>
  );
}
function GDSampleDiffBar() {
  return (
    <Box
      sx={{
        width: 56,
        height: 5,
        borderRadius: 3,
        bgcolor: `${colors.success}1a`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          width: '70%',
          bgcolor: colors.success,
          opacity: 0.7,
        }}
      />
    </Box>
  );
}
function GDSamplePortfolioTile() {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        bgcolor: '#fff',
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 1,
        px: 0.6,
        py: 0.3,
        gap: 0.05,
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: 7,
          fontWeight: 800,
          letterSpacing: '0.08em',
          color: colors.textMuted,
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        Net
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: 10,
          fontWeight: 800,
          color: colors.success,
          lineHeight: 1,
          fontFeatureSettings: '"tnum"',
        }}
      >
        +124
      </Box>
    </Box>
  );
}
